'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  useMicroSource,
  type MicroContentItem,
  type MicroProgress,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useMicroPopup } from './popup/MicroPopupHost';
import { useKindLabel } from './_kindLabel';
import { dispatchMicroProgressUpdated, dispatchMicroToast } from './_events';
import { cardReset } from '../_shared/styles';

/**
 * Shared compact card for every micro-content rail/grid block. Renders
 * a cover with a kind chip, duration badge and progress bar, then title,
 * description and a footer of kind meta (duration / readingMinutes /
 * cardCount / questionCount), category chip and topic tags.
 *
 * <p>The cover is 16:9 unless the PARENT asks for portrait — only a
 * Shorts-only surface (the shorts listing, a shorts-filtered rail) does,
 * to keep the reel identity there. A mixed rail or grid never mixes
 * ratios, so every card in a row is the same size; the tile also fills
 * its grid/flex cell so rows stay level when text lengths differ.
 *
 * <p>Micro has no detail route — with a {@code MicroPopupHost} mounted
 * the whole tile opens the player popup; the href template is only the
 * fallback for hosts without one (e.g. bare previews).
 *
 * <p>State signals (legacy parity): parents fetch the learner's rows
 * once via {@code progress.fetchMine} + {@code categories.list} and
 * pass {@code progress}/{@code categoryName}/{@code favorite} down —
 * the card never fetches per-item. The completed ✓ / in-progress pill
 * overlays the cover; the heart overlay toggles the favorite
 * optimistically (the card is itself a button when the popup host is
 * mounted, so the heart is a {@code span[role=button]} that stops
 * propagation — a nested {@code <button>} would be invalid HTML).
 */
export function MicroItemCard({
  item,
  hrefTemplate = '/lxp/micro-learning/{kindSlug}',
  className,
  progress,
  categoryName,
  favorite,
  showKindBadge = true,
  cover = 'landscape',
}: {
  item: MicroContentItem;
  hrefTemplate?: string;
  className?: string;
  /** The learner's progress row for this item (from the parent's one
   *  {@code fetchMine} read). Drives the completed/in-progress pill
   *  and the heart's initial state. */
  progress?: MicroProgress | null;
  /** Resolved category name for the footer (from the parent's one
   *  {@code categories.list} read). */
  categoryName?: string | null;
  /** Seed for the heart's initial state (e.g. the favorites rail knows
   *  every item is a favorite before fetchMine resolves). The live
   *  progress row takes precedence once it arrives. */
  favorite?: boolean;
  /** Show the content-kind chip over the cover. Operators turn it off
   *  per rail/grid from the page designer. */
  showKindBadge?: boolean;
  /** Cover ratio. Portrait (9:16) only on Shorts-only surfaces — a mixed
   *  row must not mix ratios. */
  cover?: 'landscape' | 'portrait';
}) {
  const popup = useMicroPopup();
  const micro = useMicroSource();
  const router = useRouter();
  // Category / tag chips are filters, not decoration: they open the
  // kind's listing narrowed to that value. The card itself is a button
  // (or link), so the chips are role="link" spans that stop the click
  // and navigate through the router (which adds the base path).
  const listing = `/lxp/micro-learning/${encodeURIComponent(item.kindSlug)}`;
  const goTo = (href: string) => (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(href);
  };
  const filterChip = (label: string, href: string, kind: 'category' | 'tag', title: string) => (
    <span
      key={`${kind}:${label}`}
      role="link"
      tabIndex={0}
      title={title}
      onClick={goTo(href)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') goTo(href)(e);
      }}
      style={{ ...chip(kind), cursor: 'pointer' }}
    >
      {label}
    </span>
  );
  const { t } = useTranslation('site-builder-blocks');
  const kindLabel = useKindLabel();
  const href = hrefTemplate
    .replace('{kindSlug}', encodeURIComponent(item.kindSlug))
    .replace('{id}', encodeURIComponent(String(item.id)));

  // Optimistic heart. Seeded from props, re-synced when the parent's
  // fetchMine refresh delivers new rows; reverts if the write fails.
  // The live progress row wins over the static `favorite` seed so an
  // un-favorited card doesn't snap back after the refresh.
  const initialFavorite = progress?.favorite ?? favorite ?? false;
  const [fav, setFav] = useState(initialFavorite);
  useEffect(() => setFav(initialFavorite), [initialFavorite]);
  const toggleFavorite = () => {
    if (!micro?.favorites) return;
    const next = !fav;
    setFav(next);
    void micro.favorites
      .toggle({ itemId: item.id })
      .then((serverState) => {
        setFav(serverState);
        dispatchMicroProgressUpdated();
        dispatchMicroToast(
          serverState ? t('microChrome.favorite.added') : t('microChrome.favorite.removed'),
          {
            description: t('microChrome.favorite.toastDetail', {
              kind: kindLabel(item.kind),
              title: item.title,
            }),
            tone: serverState ? 'success' : 'neutral',
          },
        );
      })
      .catch(() => setFav(!next));
  };

  const status = progress?.status;
  const isShorts = item.kind === 'SHORTS';
  const portrait = cover === 'portrait';
  const isPlayable = item.kind === 'SHORTS' || item.kind === 'SHORT_VIDEO' || item.kind === 'AUDIO';

  // Playable kinds wear their length on the cover (mm:ss, where the eye
  // expects it); everything else spells its unit out in the footer.
  const coverDuration = isPlayable && item.meta?.duration ? item.meta.duration : null;
  const metaParts: string[] = [];
  if (item.meta?.duration && !coverDuration)
    metaParts.push(
      formatDurationLabel(item.meta.duration, (n) =>
        t('microCard.meta.durationMinutes', { count: n }),
      ),
    );
  if (item.meta?.readingMinutes)
    metaParts.push(t('microCard.meta.readingMinutes', { count: item.meta.readingMinutes }));
  if (item.meta?.questionCount)
    metaParts.push(t('microCard.meta.questions', { count: item.meta.questionCount }));
  if (item.meta?.cardCount)
    metaParts.push(t('microCard.meta.cards', { count: item.meta.cardCount }));

  // Answered-so-far from the progress row: a bar while in progress, a
  // score once completed. Only for kinds that count (quiz, scenario…).
  const total = progress?.totalCount ?? 0;
  const correct = progress?.correctCount ?? 0;
  const percent = total > 0 ? Math.round((Math.min(correct, total) / total) * 100) : null;
  if (status === 'completed' && percent !== null)
    metaParts.push(t('microCard.meta.score', { correct, total }));

  const tags = (item.topicTags ?? []).slice(0, 2);
  const moreTags = Math.max(0, (item.topicTags?.length ?? 0) - tags.length);

  const tileStyle = {
    ...cardReset,
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column' as const,
    // Fill the grid / rail cell so a row of cards stays level regardless
    // of how much text each one carries.
    height: '100%',
    background: 'var(--color-surface-card, #fff)',
    borderRadius: 'var(--radius-md, 8px)',
    overflow: 'hidden',
  };

  const body = (
    <>
      <div
        style={{
          aspectRatio: portrait ? '9 / 16' : '16 / 9',
          flexShrink: 0,
          position: 'relative',
          background: item.coverImageUrl
            ? `url(${item.coverImageUrl}) center/cover no-repeat`
            : isShorts
              ? 'linear-gradient(135deg, var(--color-brand-500, #3b5bdb) 0%, var(--color-brand-700, #1e3a8a) 100%)'
              : 'linear-gradient(135deg, var(--color-brand-100, #dbeafe), var(--color-brand-50, #eff6ff))',
        }}
      >
        {(item.kind === 'SHORTS' || item.kind === 'SHORT_VIDEO') && (
          <PlayCoverOverlay hasImage={!!item.coverImageUrl} />
        )}
        {showKindBadge && (
          <span
            aria-hidden
            style={{
              // Two levels of control, on purpose. `showKindBadge` is the
              // PER-BLOCK override an operator sets on one rail; the
              // `--app-micro-kind-badge-display` design token is the
              // SITE-WIDE switch — set it to `none` in the theme and every
              // kind chip disappears at once, including the ones on
              // MicroListingLayout, which exposes no prop of its own.
              // Purely visual and already aria-hidden, so hiding it in CSS
              // costs nothing an assistive reader would miss.
              display: 'var(--app-micro-kind-badge-display, block)',
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'color-mix(in srgb, var(--color-neutral-950, #0f172a) 85%, transparent)',
              color: 'var(--color-text-on-brand, #fff)',
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-wider, 0.06em)',
              textTransform: 'uppercase',
              padding: 'var(--space-1, 4px) var(--space-2, 8px)',
              borderRadius: 4,
            }}
          >
            {kindLabel(item.kind)}
          </span>
        )}
        {micro?.favorites && (
          <span
            role="button"
            tabIndex={0}
            aria-label={fav ? t('microChrome.favorite.remove') : t('microChrome.favorite.add')}
            aria-pressed={fav}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleFavorite();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite();
              }
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.95)',
              color: fav ? 'var(--color-danger, #ef4444)' : 'var(--color-text-secondary, #475569)',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              zIndex: 2,
            }}
          >
            <HeartIcon filled={fav} />
          </span>
        )}
        {coverDuration && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              padding: '2px 6px',
              borderRadius: 4,
              background: 'color-mix(in srgb, var(--color-neutral-950, #0f172a) 85%, transparent)',
              color: 'var(--color-text-on-brand, #fff)',
              fontSize: '11px',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.4,
            }}
          >
            {coverDuration}
          </span>
        )}
        {status === 'in-progress' && percent !== null && (
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 4,
              background: 'rgba(255,255,255,0.5)',
            }}
          >
            <div
              style={{
                width: `${percent}%`,
                height: '100%',
                background: 'var(--color-warning, #f97316)',
              }}
            />
          </div>
        )}
        {status === 'in-progress' && (
          <StatusPill
            color="var(--color-warning, #f97316)"
            background="rgba(255,255,255,0.95)"
            label={t('microCard.status.inProgress')}
            dot
          />
        )}
        {status === 'completed' && (
          <StatusPill
            color="var(--color-text-on-brand, #fff)"
            background="var(--color-success-500, #16a34a)"
            label={`✓ ${t('microCard.status.completed')}`}
          />
        )}
      </div>
      <div
        style={{
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 600,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            overflow: 'hidden',
          }}
        >
          {item.title}
        </div>
        {item.description && !portrait && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-secondary, #71717a)',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }}
          >
            {item.description}
          </div>
        )}
        {(metaParts.length > 0 || categoryName || tags.length > 0) && (
          <div
            style={{
              marginTop: 'auto',
              paddingTop: 6,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              fontSize: '11px',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {metaParts.length > 0 && (
              <span style={{ fontFamily: 'var(--font-family-mono, monospace)' }}>
                {metaParts.join(' · ')}
              </span>
            )}
            {categoryName &&
              item.categoryId != null &&
              filterChip(
                categoryName,
                `${listing}?category=${encodeURIComponent(String(item.categoryId))}`,
                'category',
                t('microCard.filter.category', { name: categoryName }),
              )}
            {tags.map((tag) =>
              filterChip(
                tag,
                `${listing}?tag=${encodeURIComponent(tag)}`,
                'tag',
                t('microCard.filter.tag', { tag }),
              ),
            )}
            {moreTags > 0 && (
              <span style={chip('tag')}>{t('microCard.meta.moreTags', { count: moreTags })}</span>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (popup) {
    return (
      <Card asChild>
        <button
          type="button"
          onClick={() => popup.open(item)}
          className={className}
          style={{
            ...tileStyle,
            textAlign: 'left',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          {body}
        </button>
      </Card>
    );
  }

  return (
    <Card asChild>
      <Link href={href} className={className} style={tileStyle}>
        {body}
      </Link>
    </Card>
  );
}

/**
 * "4:18" → "4 min"-style label. Designs show rounded minutes, not
 * MM:SS; sub-minute clips read "1 min" so shorts never show "0 min"
 * (legacy formatDurationLabel). Non-MM:SS strings pass through.
 */
function formatDurationLabel(raw: string, minutesLabel: (count: number) => string): string {
  if (!raw.includes(':')) return raw;
  const parts = raw.split(':');
  const m = parseInt(parts[0]!, 10);
  const s = parseInt(parts[1]!, 10);
  if (Number.isNaN(m) || Number.isNaN(s)) return raw;
  if (m === 0) return minutesLabel(s > 0 ? 1 : 0);
  return minutesLabel(m);
}

/**
 * Cover play affordance for the video kinds (legacy parity): a white
 * play disc over a photo cover, or a large white play glyph dropped
 * straight onto the gradient when there is no image.
 */
function PlayCoverOverlay({ hasImage }: { hasImage: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {hasImage ? (
        <div
          style={{
            background: 'rgba(255,255,255,0.95)',
            borderRadius: 999,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--color-brand-600, #7c3aed)">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      ) : (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="rgba(255,255,255,0.92)">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </div>
  );
}

/** Footer chips — the category reads as brand, topic tags as neutral. */
function chip(kind: 'category' | 'tag'): CSSProperties {
  return {
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: 999,
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    background: kind === 'category' ? 'var(--color-brand-50, #eff6ff)' : 'transparent',
    color: kind === 'category' ? 'var(--color-brand-700, #1d4ed8)' : 'inherit',
    border:
      kind === 'category'
        ? '1px solid transparent'
        : '1px solid var(--color-border, rgba(0,0,0,0.12))',
  };
}

/** Bottom-right cover pill for completed / in-progress state. */
function StatusPill({
  color,
  background,
  label,
  dot,
}: {
  color: string;
  background: string;
  label: string;
  dot?: boolean;
}) {
  const style: CSSProperties = {
    position: 'absolute',
    bottom: 8,
    right: 8,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background,
    color,
    fontSize: '11px',
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 999,
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    whiteSpace: 'nowrap',
  };
  return (
    <span style={style}>
      {dot && (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: 'currentColor',
            display: 'inline-block',
          }}
        />
      )}
      {label}
    </span>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
